<script setup>
import { ref, computed, nextTick, useSlots, onBeforeUnmount } from "vue"
import { useI18n } from "vue-i18n"

const props = defineProps({
  modelValue: {
    type: [Object, String, Number, Array],
    default: null,
  },

  // Multiple: modelValue is an array, clicking an item toggles it and keeps the dialog open
  multiple: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array,
    default: () => [],
  },
  itemTitle: {
    type: [String, Function],
    default: "title",
  },
  itemValue: {
    type: [String, Function],
    default: "value",
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  innerLabel: {
    type: String,
    default: "",
  },
  innerPlaceholder: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  returnObject: {
    type: Boolean,
    default: false,
  },
  noFilter: {
    type: Boolean,
    default: false,
  },
  customFilter: {
    type: Function,
    default: null,
  },
  color: {
    type: String,
    default: "secondary",
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  hideNoData: {
    type: Boolean,
    default: false,
  },
  noDataText: {
    type: String,
    default: "",
  },
  rules: {
    type: Array,
    default: () => [],
  },
  density: {
    type: String,
    default: "compact",
  },
  class: {
    type: String,
    default: "",
  },
  textAreaId: {
    type: String,
    default: "",
  },
  textAreaClass: {
    type: String,
    default: "",
  },
})
const emit = defineEmits(["update:modelValue", "update:search", "blur"])
const { t } = useI18n()
const slots = useSlots()

const isOpen = ref(false)
const searchQuery = ref("")
const searchInputRef = ref(null)
const listContainerRef = ref(null)

const scrollListToTop = () => {
  const listEl = listContainerRef.value

  if (!listEl) return

  listEl.scrollTop = 0
}

const getItemTitle = item => {
  if (item == null) return ""
  if (typeof props.itemTitle === "function") return props.itemTitle(item)

  return item[props.itemTitle] ?? ""
}

const getItemValue = item => {
  if (item == null) return ""
  if (typeof props.itemValue === "function") return props.itemValue(item)

  return item[props.itemValue] ?? ""
}

const displayValue = computed(() => {
  if (props.modelValue == null) return ""
  if (props.returnObject) return getItemTitle(props.modelValue)

  const found = props.items.find(i => getItemValue(i) === props.modelValue)

  return found ? getItemTitle(found) : String(props.modelValue)
})

const displayItems = computed(() => {
  if (props.noFilter) return props.items
  if (!searchQuery.value) return props.items

  if (props.customFilter) {
    return props.items.filter(item => {
      const title = getItemTitle(item)

      return props.customFilter(title, searchQuery.value, { raw: item })
    })
  }

  const query = searchQuery.value.toLowerCase()

  return props.items.filter(item => {
    const title = getItemTitle(item)

    return title.toLowerCase().includes(query)
  })
})

const HISTORY_STATE_KEY = "mobileAutocompleteOpen"

/**
 * Handle pop state event when user navigates back from the dialog (back button on device)
 */
const onPopState = () => {
  if (isOpen.value) {
    isOpen.value = false
    searchQuery.value = ""
    emit("blur")
  }
}
let list = null

const handleTouchMove = (e) => {
  if (list && !list.contains(e.target)) {
    e.preventDefault()
  }
}

const openDialog = () => {
  if (props.disabled) return
  isOpen.value = true
  searchQuery.value = ""
  emit("update:search", "")

  history.pushState({ [HISTORY_STATE_KEY]: true }, "")
  window.addEventListener("popstate", onPopState, { once: true })
  //console.log('openDialog history.state?.[mobileAutocompleteOpen]', history.state?.['mobileAutocompleteOpen'])
  nextTick(() => {
    scrollListToTop()
    searchInputRef.value?.focus()
    // iOS Safari can restore scroll position after focus/keyboard animation.
    setTimeout(scrollListToTop, 80)
    document.body.style.overflow = 'hidden'
    document.body.style.height = '100dvh'

    setRealHeight()
    window.visualViewport?.addEventListener('resize', setRealHeight)

    //list = document.querySelector('.mobile-autocomplete-list')

    //document.addEventListener('touchmove', handleTouchMove, { passive: false })

  })
}

const closeDialog = () => {
  if (!isOpen.value) return
  isOpen.value = false
  searchQuery.value = ""
  emit("blur")

  window.removeEventListener("popstate", onPopState)
  document.body.style.overflow = ''
  document.body.style.height = ''

  //document.removeEventListener('touchmove', handleTouchMove)

  // if (history.state?.[HISTORY_STATE_KEY]) {
  //   //history.back()
  // }
}

const selectItem = item => {
  const value = props.returnObject ? item : getItemValue(item)

  if (props.multiple) {
    const selected = props.modelValue ?? []
    const key = getItemValue(item)
    const isSelected = selected.some(v => (props.returnObject ? getItemValue(v) : v) === key)

    emit("update:modelValue", isSelected
      ? selected.filter(v => (props.returnObject ? getItemValue(v) : v) !== key)
      : [...selected, value])

    return
  }

  emit("update:modelValue", value)
  closeDialog()
}

defineExpose({ closeDialog })

onBeforeUnmount(() => {
  window.removeEventListener("popstate", onPopState)
  if (isOpen.value && history.state?.[HISTORY_STATE_KEY]) {
    //history.back()
    history.replaceState({}, "")
  }
  document.body.style.overflow = ''
  document.body.style.height = ''

  //document.removeEventListener('touchmove', handleTouchMove)

})

const clearSelection = e => {
  e?.stopPropagation()
  emit("update:modelValue", null)
}

const onSearchInput = val => {
  emit("update:search", val)
}


const setRealHeight = ()  => {
  document.documentElement.style.setProperty(
    '--real-vh',
    `${window.visualViewport.height}px`
  )
}
</script>

<template>
  <!-- Custom trigger -->
  <slot
    v-if="slots.activator"
    name="activator"
    :open="openDialog"
  />
  <!-- Trigger: looks like a Vuetify select field -->
  <div
    v-else
    class="mobile-autocomplete-trigger"
    :class="props.class"
    @click="openDialog"
  >
    <VTextarea
      :id="props.textAreaId"
      :class="props.textAreaClass"
      :model-value="displayValue"
      readonly
      :label="label"
      :placeholder="!displayValue ? placeholder : undefined"
      :loading="loading"
      :disabled="disabled"
      :density="density"
      :color="color"
      :rules="rules"
      hide-spin-buttons
      auto-grow
      rows="1"
    >
      <template
        v-if="slots['prepend-inner']"
        #prepend-inner
      >
        <slot name="prepend-inner" />
      </template>

      <template #append-inner>
        <VIcon
          v-if="modelValue != null && clearable"
          size="18"
          icon="ri-close-line"
          class="me-1 cursor-pointer"
          @click.stop="clearSelection"
        />
        <VIcon
          size="18"
          icon="ri-arrow-down-s-line"
          style="opacity: 0.5"
        />
      </template>
    </VTextarea>
  </div>
  <!-- Fullscreen Dialog -->
  <Teleport to="body">
    <Transition name="mobile-autocomplete-transition">
      <div
        v-if="isOpen"
        class="mobile-autocomplete-overlay"
        role="dialog"
        aria-modal="true"
      >
        <div class="mobile-autocomplete-card d-flex flex-column">
          <!-- Search Header (always visible, never scrolls) -->
          <div class="d-flex align-center pa-3 gap-2 mobile-autocomplete-header flex-shrink-0">
            <VBtn
              icon
              variant="text"
              density="compact"
              @click="closeDialog"
            >
              <VIcon icon="ri-arrow-left-line" />
            </VBtn>

            <VTextField
              ref="searchInputRef"
              v-model="searchQuery"
              :placeholder="innerPlaceholder"
              :label="innerLabel"
              density="compact"
              hide-details
              autofocus
              clearable
              variant="outlined"
              :color="color"
              class="flex-grow-1"
              @update:model-value="onSearchInput"
            >
              <template #prepend-inner>
                <VIcon
                  size="18"
                  icon="ri-search-line"
                  style="opacity: 0.5"
                />
              </template>
            </VTextField>
          </div>

          <VDivider class="flex-shrink-0" />

          <VProgressLinear
            v-if="loading"
            indeterminate
            :color="color"
            height="2"
          />

          <!-- Items List (scrollable, takes remaining space) -->
          <div
            ref="listContainerRef"
            class="mobile-autocomplete-list"
          >
            <VList
              v-if="displayItems.length > 0"
              density="compact"
            >
              <template
                v-for="(item, index) in displayItems"
                :key="getItemValue(item) ?? index"
              >
                <slot
                  name="item"
                  :item="{ raw: item, title: getItemTitle(item), value: getItemValue(item) }"
                  :props="{ onClick: () => selectItem(item), title: getItemTitle(item) }"
                >
                  <VListItem @click="selectItem(item)">
                    <VListItemTitle class="text-wrap">
                      {{ getItemTitle(item) }}
                    </VListItemTitle>
                  </VListItem>
                </slot>
              </template>
            </VList>

            <!-- No data message -->
            <div
              v-else-if="!hideNoData && searchQuery && !loading"
              class="pa-4 text-center text-medium-emphasis"
            >
              {{ noDataText || t('no_results') }}
            </div>
          </div>
          <button id="omg">↑↑</button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.mobile-autocomplete-trigger :deep(input) {
  cursor: pointer;
}

.mobile-autocomplete-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgb(var(--v-theme-surface));
}

.mobile-autocomplete-card {
  height: var(--real-vh);
  overflow: hidden !important;
}

.mobile-autocomplete-header {
  z-index: 1;
  width: 100%;
  background: rgb(var(--v-theme-surface));
}

.mobile-autocomplete-list {
  flex: 1;
/*  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  height: var(--real-vh);
  max-height: var(--real-vh);*/
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  height: var(--real-vh);
  max-height: var(--real-vh);
  padding-bottom: 10px;
}

.mobile-autocomplete-transition-enter-active,
.mobile-autocomplete-transition-leave-active {
  transition: transform .2s ease, opacity .2s ease;
}

.mobile-autocomplete-transition-enter-from,
.mobile-autocomplete-transition-leave-to {
  opacity: 0;
  transform: translateY(24px);
}
</style>
